import React, { useState } from "react";
import Header from "../_components/_global/WelcomeHeader";

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [expandedFaq, setExpandedFaq] = useState(null);

  const pricingPlans = [
    {
      name: "Free",
      price: "$0",
      period: "/forever",
      badge: "Start Free",
      features: [
        { text: "Basic city planning tools", included: true },
        { text: "Access to public itineraries", included: true },
        { text: "Community travel tips", included: true },
        { text: "Up to 3 saved trips", included: true },
        { text: "Standard support", included: true },
      ],
    },
    {
      name: "Pro",
      price: "$10",
      period: "/month",
      badge: null,
      popular: true,
      features: [
        { text: "Advanced AI itinerary builder", included: true },
        { text: "Unlimited saved trips", included: true },
        { text: "Offline access & maps", included: true },
        { text: "Priority support", included: true },
        { text: "Multi-city trip planning", included: true },
        { text: "Real-time collaboration", included: true },
        { text: "Exclusive local deals", included: true },
      ],
    },
    {
      name: "Business",
      price: "$25",
      period: "/month",
      badge: "Contact Sales",
      features: [
        { text: "Everything in Pro", included: true },
        { text: "Team collaboration tools", included: true },
        { text: "Dedicated account manager", included: true },
        { text: "Custom branding options", included: true },
        { text: "Advanced analytics", included: true },
        { text: "API access", included: true },
        { text: "SLA guarantee", included: true },
      ],
    },
  ];

  const comparisonFeatures = [
    { name: "Basic city planning", free: true, pro: true, business: true },
    { name: "AI itinerary builder", free: false, pro: true, business: true },
    {
      name: "Saved trips",
      free: "Up to 3",
      pro: "Unlimited",
      business: "Unlimited",
    },
    { name: "Offline maps", free: false, pro: true, business: true },
    { name: "Real-time navigation", free: false, pro: true, business: true },
    { name: "Multi-city planning", free: false, pro: true, business: true },
    { name: "Team collaboration", free: false, pro: false, business: true },
    { name: "Priority support", free: false, pro: true, business: true },
    {
      name: "Dedicated account manager",
      free: false,
      pro: false,
      business: true,
    },
    { name: "Custom branding", free: false, pro: false, business: true },
  ];

  const faqs = [
    {
      question: "Can I try Pro before purchasing?",
      answer:
        "Yes! We offer a 14-day free trial of Pro with no credit card required. You can explore all premium features before committing.",
    },
    {
      question: "What's your refund policy?",
      answer:
        "We offer a 30-day money-back guarantee for all paid plans. If you're not satisfied, contact support for a full refund within 30 days of purchase.",
    },
    {
      question: "Can I switch between monthly and yearly billing?",
      answer:
        "Absolutely! You can switch billing cycles at any time. When upgrading to yearly, we'll prorate the remaining balance of your monthly subscription.",
    },
    {
      question: "Do you offer student or non-profit discounts?",
      answer:
        "Yes! Students get 50% off Pro with valid student ID. Non-profit organizations qualify for 40% discount. Contact sales@citygetaway.com for verification.",
    },
    {
      question: "How do I get an invoice for my subscription?",
      answer:
        "Invoices are sent automatically to your email after each payment. You can also download them anytime from your account settings under 'Billing & Invoices.'",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards (Visa, Mastercard, Amex), PayPal, and Apple Pay. Business plans can also pay via invoice and bank transfer.",
    },
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: "Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Helvetica Neue", Arial, sans-serif;
          background: #ffffff;
          color: #1a1a1a;
        }
        
        .pricing-container {
          min-height: 100vh;
          background: #ffffff;
          padding-top: 80px;
        }
        
        .pricing-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 60px 20px;
        }
        
        /* Hero Section */
        .pricing-hero {
          text-align: center;
          margin-bottom: 50px;
        }
        
        .pricing-title {
          font-size: 56px;
          font-weight: 600;
          color: #0f1720;
          margin-bottom: 20px;
          line-height: 1.2;
        }
        
        .pricing-subtitle {
          font-size: 20px;
          color: #6b7280;
          max-width: 600px;
          margin: 0 auto;
        }
        
        /* Billing Toggle */
        .billing-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin-bottom: 50px;
        }
        
        .billing-label {
          font-size: 16px;
          color: #6b7280;
        }
        
        .toggle-switch {
          position: relative;
          width: 56px;
          height: 28px;
          background: #e5e7eb;
          border-radius: 20px;
          cursor: pointer;
          transition: background 0.3s;
        }
        
        .toggle-switch.yearly {
          background: #3b82f6;
        }
        
        .toggle-slider {
          position: absolute;
          top: 3px;
          left: 3px;
          width: 22px;
          height: 22px;
          background: white;
          border-radius: 50%;
          transition: transform 0.3s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .toggle-switch.yearly .toggle-slider {
          transform: translateX(28px);
        }
        
        .save-badge {
          background: #10b981;
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 500;
        }
        
        /* Pricing Cards */
        .pricing-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin-bottom: 80px;
        }
        
        .pricing-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 32px;
          position: relative;
          transition: all 0.3s;
        }
        
        .pricing-card:hover {
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          transform: translateY(-4px);
        }
        
        .pricing-card.popular {
          border-color: #3b82f6;
          border-width: 2px;
        }
        
        .popular-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: #3b82f6;
          color: white;
          padding: 4px 16px;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 500;
        }
        
        .plan-name {
          font-size: 20px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }
        
        .plan-price {
          display: flex;
          align-items: baseline;
          margin-bottom: 8px;
        }
        
        .price-currency {
          font-size: 24px;
          color: #1f2937;
        }
        
        .price-amount {
          font-size: 48px;
          font-weight: 600;
          color: #0f1720;
        }
        
        .price-period {
          font-size: 16px;
          color: #6b7280;
          margin-left: 4px;
        }
        
        .plan-badge {
          font-size: 14px;
          color: #6b7280;
          margin-bottom: 32px;
          height: 20px;
        }
        
        .features-list {
          list-style: none;
          margin-bottom: 32px;
        }
        
        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 0;
        }
        
        .feature-icon {
          color: #10b981;
          font-size: 16px;
          margin-top: 2px;
        }
        
        .feature-text {
          color: #4b5563;
          font-size: 15px;
          flex: 1;
        }
        
        .plan-button {
          width: 100%;
          padding: 12px 24px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s;
          border: 1px solid #e5e7eb;
          background: white;
          color: #1f2937;
        }
        
        .plan-button:hover {
          background: #f9fafb;
        }
        
        .plan-button.primary {
          background: #3b82f6;
          color: white;
          border: none;
        }
        
        .plan-button.primary:hover {
          background: #2563eb;
        }
        
        /* Comparison Table */
        .comparison-section {
          margin-bottom: 80px;
        }
        
        .comparison-title {
          font-size: 32px;
          font-weight: 600;
          color: #0f1720;
          text-align: center;
          margin-bottom: 16px;
        }
        
        .comparison-subtitle {
          font-size: 18px;
          color: #6b7280;
          text-align: center;
          margin-bottom: 48px;
        }
        
        .comparison-table {
          width: 100%;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .table-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          background: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          padding: 16px;
        }
        
        .table-header-cell {
          font-weight: 600;
          color: #1f2937;
          font-size: 16px;
          text-align: center;
        }
        
        .table-header-cell:first-child {
          text-align: left;
        }
        
        .table-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          padding: 16px;
          border-bottom: 1px solid #f3f4f6;
          align-items: center;
        }
        
        .table-row:last-child {
          border-bottom: none;
        }
        
        .table-cell {
          text-align: center;
          font-size: 15px;
        }
        
        .table-cell:first-child {
          text-align: left;
          color: #4b5563;
        }
        
        .check-icon {
          color: #10b981;
          font-size: 18px;
        }
        
        .x-icon {
          color: #d1d5db;
          font-size: 18px;
        }
        
        .table-value {
          color: #4b5563;
          font-size: 14px;
        }
        
        /* Benefits Section */
        .benefits-section {
          background: #f9fafb;
          margin: 0 -20px;
          padding: 60px 20px;
          margin-bottom: 80px;
        }
        
        .benefits-grid {
          max-width: 1000px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 48px;
          text-align: center;
        }
        
        .benefit-item {
          padding: 0 20px;
        }
        
        .benefit-icon {
          font-size: 32px;
          margin-bottom: 16px;
        }
        
        .benefit-title {
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 8px;
        }
        
        .benefit-description {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.6;
        }
        
        /* FAQ Section */
        .faq-section {
          max-width: 800px;
          margin: 0 auto 80px;
        }
        
        .faq-title {
          font-size: 32px;
          font-weight: 600;
          color: #0f1720;
          text-align: center;
          margin-bottom: 16px;
        }
        
        .faq-subtitle {
          font-size: 18px;
          color: #6b7280;
          text-align: center;
          margin-bottom: 48px;
        }
        
        .faq-list {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .faq-item {
          border-bottom: 1px solid #e5e7eb;
        }
        
        .faq-item:last-child {
          border-bottom: none;
        }
        
        .faq-question {
          width: 100%;
          padding: 20px 24px;
          background: white;
          border: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          transition: background 0.2s;
          text-align: left;
        }
        
        .faq-question:hover {
          background: #f9fafb;
        }
        
        .faq-question-text {
          font-size: 16px;
          font-weight: 500;
          color: #1f2937;
          flex: 1;
        }
        
        .faq-arrow {
          font-size: 20px;
          color: #6b7280;
          transition: transform 0.3s;
        }
        
        .faq-arrow.expanded {
          transform: rotate(180deg);
        }
        
        .faq-answer {
          padding: 0 24px;
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s ease;
          background: white;
        }
        
        .faq-answer.expanded {
          padding: 0 24px 20px;
          max-height: 200px;
        }
        
        .faq-answer-text {
          font-size: 15px;
          color: #6b7280;
          line-height: 1.6;
        }
        
        /* Compare Link */
        .compare-link {
          text-align: center;
          margin-top: 24px;
        }
        
        .compare-link a {
          color: #3b82f6;
          text-decoration: none;
          font-size: 16px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 4px;
          transition: gap 0.2s;
        }
        
        .compare-link a:hover {
          gap: 8px;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .pricing-title {
            font-size: 36px;
          }
          
          .pricing-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .table-header,
          .table-row {
            grid-template-columns: 2fr 1fr 1fr 1fr;
            font-size: 14px;
          }
          
          .benefits-grid {
            grid-template-columns: 1fr;
            gap: 32px;
          }
        }
      `}</style>

      <Header />

      <div className="pricing-container">
        <div className="pricing-content">
          {/* Hero Section */}
          <div className="pricing-hero">
            <h1 className="pricing-title">Simple, transparent pricing</h1>
            <p className="pricing-subtitle">
              Choose the plan that fits your travel needs. Upgrade or downgrade
              anytime.
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="billing-toggle">
            <span
              className={`billing-label ${
                billingCycle === "monthly" ? "active" : ""
              }`}
            >
              Monthly
            </span>
            <div
              className={`toggle-switch ${
                billingCycle === "yearly" ? "yearly" : ""
              }`}
              onClick={() =>
                setBillingCycle(
                  billingCycle === "monthly" ? "yearly" : "monthly"
                )
              }
            >
              <div className="toggle-slider"></div>
            </div>
            <span
              className={`billing-label ${
                billingCycle === "yearly" ? "active" : ""
              }`}
            >
              Yearly
            </span>
            {billingCycle === "yearly" && (
              <span className="save-badge">Save 20%</span>
            )}
          </div>

          {/* Pricing Cards */}
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`pricing-card ${plan.popular ? "popular" : ""}`}
              >
                {plan.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price">
                  <span className="price-amount">{plan.price}</span>
                  <span className="price-period">{plan.period}</span>
                </div>
                <div className="plan-badge">{plan.badge || "\u00A0"}</div>
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">{feature.text}</span>
                    </li>
                  ))}
                </ul>
                <button
                  className={`plan-button ${plan.popular ? "primary" : ""}`}
                >
                  {plan.name === "Free"
                    ? "Start Free"
                    : plan.name === "Business"
                    ? "Contact Sales"
                    : "Start Free Trial"}
                </button>
              </div>
            ))}
          </div>

          <div className="compare-link">
            <a href="#compare">Compare all features →</a>
          </div>

          {/* Comparison Table */}
          <div className="comparison-section" id="compare">
            <h2 className="comparison-title">Compare plans</h2>
            <p className="comparison-subtitle">
              See which features are included in each plan
            </p>
            <div className="comparison-table">
              <div className="table-header">
                <div className="table-header-cell">Feature</div>
                <div className="table-header-cell">Free</div>
                <div className="table-header-cell">Pro</div>
                <div className="table-header-cell">Business</div>
              </div>
              {comparisonFeatures.map((feature, index) => (
                <div key={index} className="table-row">
                  <div className="table-cell">{feature.name}</div>
                  <div className="table-cell">
                    {typeof feature.free === "boolean" ? (
                      feature.free ? (
                        <span className="check-icon">✓</span>
                      ) : (
                        <span className="x-icon">✕</span>
                      )
                    ) : (
                      <span className="table-value">{feature.free}</span>
                    )}
                  </div>
                  <div className="table-cell">
                    {typeof feature.pro === "boolean" ? (
                      feature.pro ? (
                        <span className="check-icon">✓</span>
                      ) : (
                        <span className="x-icon">✕</span>
                      )
                    ) : (
                      <span className="table-value">{feature.pro}</span>
                    )}
                  </div>
                  <div className="table-cell">
                    {typeof feature.business === "boolean" ? (
                      feature.business ? (
                        <span className="check-icon">✓</span>
                      ) : (
                        <span className="x-icon">✕</span>
                      )
                    ) : (
                      <span className="table-value">{feature.business}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits Section */}
          <div className="benefits-section">
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6b7280"
                    strokeWidth="2"
                  >
                    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.3 1.5 4.05 3 5.5Z" />
                    <path d="M12 14v7" />
                    <path d="M16 18H8" />
                  </svg>
                </div>
                <h3 className="benefit-title">Cancel anytime</h3>
                <p className="benefit-description">
                  No long-term commitments. Cancel your subscription whenever
                  you want.
                </p>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6b7280"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="10" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
                <h3 className="benefit-title">Secure payment</h3>
                <p className="benefit-description">
                  Your payment information is encrypted and secure with industry
                  standards.
                </p>
              </div>
              <div className="benefit-item">
                <div className="benefit-icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#6b7280"
                    strokeWidth="2"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 5L2 7" />
                  </svg>
                </div>
                <h3 className="benefit-title">Email support</h3>
                <p className="benefit-description">
                  Our support team is here to help. Get responses within 24
                  hours.
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h2 className="faq-title">Frequently asked questions</h2>
            <p className="faq-subtitle">
              Everything you need to know about pricing and billing
            </p>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="faq-question-text">{faq.question}</span>
                    <span
                      className={`faq-arrow ${
                        expandedFaq === index ? "expanded" : ""
                      }`}
                    >
                      ⌄
                    </span>
                  </button>
                  <div
                    className={`faq-answer ${
                      expandedFaq === index ? "expanded" : ""
                    }`}
                  >
                    <p className="faq-answer-text">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
